import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Modal from "../components/modal";
import { getPrinters, createPrinter } from "../services/printerservice";
import { getClientes } from "../services/clientesservice";

export default function Printers() {
  const [printers, setPrinters] = useState([]);
  const [clientesList, setClientesList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  
  const initialForm = { model: "", serial_number: "", counter: 0, counter_color: 0, client_id: "", notes: "" };
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    async function loadData() {
      try {
        const [p, c] = await Promise.all([getPrinters(), getClientes()]);
        const validP = Array.isArray(p) ? p : [];
        const uniquePrinters = Array.from(new Map(validP.map(item => [item.id, item])).values());
        
        setPrinters(uniquePrinters);
        setClientesList(Array.isArray(c) ? c : []);
      } catch (error) {
        console.error("Erro crítico ao sincronizar ativos:", error);
      }
    }
    loadData();
  }, []);

  async function handleCreate() {
    if (!form.model || !form.client_id) return alert("Erro: Modelo e Cliente são obrigatórios.");

    const payload = { 
      ...form, 
      counter: parseInt(form.counter) || 0, 
      counter_color: parseInt(form.counter_color) || 0,
      client_id: parseInt(form.client_id) 
    };

    try {
      await createPrinter(payload);
      setOpenModal(false);
      setForm(initialForm);
      window.location.reload(); 
    } catch (err) {
      alert("Falha na comunicação com o servidor ao registrar ativo.");
    }
  }

  // 🚀 MANTIDA INTEGRALMENTE: Sua lógica de Agrupamento e Ordenação (Maior Qtd -> Menor Qtd)
  const groupedData = useMemo(() => {
    const groups = {};
    const term = searchTerm.toLowerCase().trim();

    printers.forEach(p => {
      if (p.client_id === null || p.client_name?.toLowerCase().includes("laboratório")) return;

      const matchesSearch = 
        p.model?.toLowerCase().includes(term) || 
        p.serial?.toLowerCase().includes(term) ||
        p.client_name?.toLowerCase().includes(term);

      if (term && !matchesSearch) return;

      const clientName = p.client_name || "CLIENTE NÃO IDENTIFICADO";
      if (!groups[clientName]) {
        groups[clientName] = { id: p.client_id, name: clientName, units: [] };
      }
      groups[clientName].units.push(p);
    });

    return Object.values(groups).sort((a, b) => {
      if (b.units.length !== a.units.length) {
        return b.units.length - a.units.length;
      }
      return a.name.localeCompare(b.name);
    });
  }, [printers, searchTerm]);

  const stats = {
    contracts: groupedData.length,
    machines: groupedData.reduce((acc, g) => acc + g.units.length, 0)
  };

  return (
    <div className="p-2 md:p-8 min-h-screen bg-[#f1f5f9] animate-in fade-in duration-500 pb-24 lg:pb-8">

      {/* HEADER RESPONSIVO */}
      <div className="max-w-[1600px] mx-auto mb-6 md:mb-10 bg-white border border-slate-200 p-4 md:p-6 rounded-[20px] md:rounded-2xl shadow-sm flex flex-col xl:flex-row justify-between items-center gap-6">
        <div className="text-center xl:text-left">
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tighter uppercase">
            Ativos em <span className="text-indigo-600">Clientes</span>
          </h1>
          <div className="flex flex-wrap justify-center xl:justify-start gap-3 md:gap-4 mt-2">
            <span className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
               📂 {stats.contracts} Contratos
            </span>
            <span className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
               📟 {stats.machines} Máquinas
            </span>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-3 w-full xl:w-auto">
          <div className="relative group w-full xl:w-96">
            <input 
              placeholder="Pesquisar..." 
              className="w-full p-3.5 md:p-4 pl-12 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:bg-white focus:border-indigo-400 focus:ring-4 ring-indigo-500/10 transition-all uppercase"
              onChange={e => setSearchTerm(e.target.value)}
            />
            <span className="absolute left-4 top-3.5 md:top-4 text-sm opacity-30 group-focus-within:opacity-100 group-focus-within:text-indigo-600">🔍</span>
          </div>
          
          <button onClick={() => setOpenModal(true)} className="w-full md:w-auto px-6 md:px-8 py-3.5 md:py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg hover:bg-indigo-700 transition-all active:scale-95 flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest">
            + Nova Implantação
          </button>
        </div>
      </div>

      {/* GRID DE CARDS - Adaptável de 1 a 4 colunas */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 items-start">
        {groupedData.length > 0 ? groupedData.map((client) => (
          
          <div key={client.name} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:border-indigo-200 transition-all flex flex-col overflow-hidden">
            <div className="bg-slate-900 p-3 md:p-4 flex justify-between items-center">
                <h3 className="text-[9px] md:text-[10px] font-black text-white uppercase tracking-widest truncate pr-2" title={client.name}>
                    {client.name}
                </h3>
                <span className="bg-indigo-500 text-white px-2 py-0.5 rounded-md text-[8px] md:text-[9px] font-black shadow-sm shrink-0">
                    {client.units.length} UN
                </span>
            </div>

            <div className="p-2 space-y-1 bg-slate-50/30">
                <div className={`p-1 space-y-1.5 overflow-y-auto custom-scrollbar ${client.units.length > 4 ? 'h-[200px] md:h-[250px]' : 'h-auto'}`}>
                    {client.units.map(unit => (
                      <div key={unit.id} className="bg-white border border-slate-200 p-2.5 md:p-3 rounded-lg flex justify-between items-center hover:border-indigo-400 transition-colors group">
                        <div className="overflow-hidden">
                          <p className="text-[10px] md:text-[11px] font-black text-slate-800 uppercase tracking-tight truncate leading-none">{unit.model}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <span className="text-[8px] font-mono font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 truncate max-w-[100px]">SN: {unit.serial || "S/S"}</span>
                            <span className="text-[8px] font-black text-slate-400">ID:{unit.id}</span>
                          </div>
                        </div>
                        <Link to={`/printers/${unit.id}`} className="ml-2 w-8 h-8 md:w-9 md:h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-900 hover:text-white transition-all shrink-0 shadow-sm" title="Prontuário">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    ))}
                </div>
            </div>
          </div>

        )) : (
          <div className="col-span-full py-20 md:py-32 text-center bg-white border border-dashed border-slate-300 rounded-2xl md:rounded-3xl">
            <p className="text-slate-400 font-black uppercase text-[10px] md:text-xs tracking-[0.3em]">Nenhum ativo vinculado a clientes</p>
          </div>
        )}
      </div>
            
      {/* MODAL DE CADASTRO - Ajustado para telas pequenas */}
      <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
        <div className="p-1 md:p-2 animate-in zoom-in-95 duration-200">
          <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-slate-900">Nova Implantação</h2>
            <button onClick={() => setOpenModal(false)} className="text-slate-400 hover:text-rose-500 text-2xl font-black transition-colors">✕</button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[9px] font-black uppercase text-slate-500 mb-1 ml-1 block">Modelo Comercial</label>
                  <input placeholder="Ex: Ricoh MP 301" className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl font-black text-slate-800 text-xs focus:bg-white focus:border-indigo-500 outline-none uppercase transition-all" onChange={e => setForm({...form, model: e.target.value})} />
                </div>
                <div>
                  <label className="text-[9px] font-black uppercase text-slate-500 mb-1 ml-1 block">Número de Série</label>
                  <input placeholder="Ex: BR1234..." className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-800 text-xs focus:bg-white focus:border-indigo-500 outline-none transition-all uppercase" onChange={e => setForm({...form, serial_number: e.target.value})} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
              <div>
                <label className="text-[9px] font-black uppercase text-slate-600 mb-1 block text-center">Início P&B</label>
                <input type="number" placeholder="0" className="w-full p-3 bg-white border border-slate-300 rounded-lg font-black text-slate-900 outline-none text-center text-base" onChange={e => setForm({...form, counter: e.target.value})} />
              </div>
              <div>
                <label className="text-[9px] font-black uppercase text-indigo-600 mb-1 block text-center">Início Color</label>
                <input type="number" placeholder="0" className="w-full p-3 bg-white border border-indigo-200 rounded-lg font-black text-indigo-700 outline-none text-center text-base" onChange={e => setForm({...form, counter_color: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-[9px] font-black uppercase text-slate-500 mb-1 ml-1 block">Destinatário</label>
              <select className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl font-black text-slate-800 text-[10px] focus:bg-white focus:border-indigo-500 outline-none uppercase cursor-pointer" onChange={e => setForm({...form, client_id: e.target.value})}>
                <option value="">SELECIONE O CLIENTE...</option>
                {clientesList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <button onClick={handleCreate} className="w-full py-4 md:py-5 bg-slate-900 text-white font-black rounded-xl shadow-lg hover:bg-indigo-600 transition-all uppercase text-[10px] tracking-widest active:scale-95 mt-4">
              Finalizar Implantação
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}